import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { TileLayout, Card, CardHeader, CardTitle, CardBody } from "@progress/kendo-react-layout";
import { addNotificationWithTimeout } from '../../app/addNotificationWithTimeout';
import { handleResponse } from '../../app/handleResponse';
import { Skeleton } from "@progress/kendo-react-indicators";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { getCSRFToken } from '../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";

import './Home.scss';

export const AdministratorHome = () => {
    const dispatch = useDispatch();

    const itemsPerPage = 10;

    const [isLoading, setIsLoading] = useState(true);
    const [eventAlerts, setEventAlerts] = useState([]);
    const [eventStatusChanges, setEventStatusChanges] = useState([]);
    const [ticketingWebsiteAlertIssues, setTicketingWebsiteAlertIssues] = useState([]);

    const [ticketingWebsiteAlertIssuesSkipCount, setTicketingWebsiteAlertIssuesSkipCount] = useState(0);
    const [eventAlertsSkipCount, setEventAlertsSkipCount] = useState(0);
    const [eventStatusChangesSkipCount, setEventStatusChangesSkipCount] = useState(0);

    const [showDialog, setShowDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");

    const ticketingWebsiteAlertIssuesPageChange = (event) => {
        setTicketingWebsiteAlertIssuesSkipCount(event.page.skip);
    };

    const eventAlertsPageChange = (event) => {
        setEventAlertsSkipCount(event.page.skip);
    };

    const eventStatusChangesPageChange = (event) => {
        setEventStatusChangesSkipCount(event.page.skip);
    };

    const ViewDialogCell = (props) => {  
        const content = props.dataItem[props.field];
        const title = props.field[0].toUpperCase() + props.field.slice(1);        

        return (
            <td className={"text-nowrap"}>
                {content ? <Button fillMode="link" type={"button"} onClick={() => viewDialog(title, content)}>View Notes</Button> : ""}
            </td>
        );
    };

    const ViewUrlCell = (props) => {  
        const url = props.dataItem[props.field];     

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const viewDialog = (title, content) => {
        setDialogTitle(title);
        setDialogContent(content);
        setShowDialog(true);
    }

    const skeletonTiles = [
        {
            header: <Skeleton shape={"text"} />,
            body: (
                <div>
                    <Skeleton
                        shape={"rectangle"}
                        style={{
                          width: "100%",
                          height: 200,
                        }}
                    />
                </div>
            ),
            resizable: false,
            reorderable: false,
        },
        {
            header: <Skeleton shape={"text"} />,
            body: (
                <div>
                    <Skeleton
                        shape={"rectangle"}
                        style={{
                          width: "100%",
                          height: 200,
                        }}
                    />
                </div>
            ),
            resizable: false,
            reorderable: false,
        },
        {
            header: <Skeleton shape={"text"} />,
            body: (
                <div>
                    <Skeleton
                        shape={"rectangle"}
                        style={{
                          width: "100%",
                          height: 200,
                        }}
                    />
                </div>
            ),
            resizable: false,
            reorderable: false,
        },
        {
            header: <Skeleton shape={"text"} />,
            body: (
                <div>
                    <Skeleton
                        shape={"rectangle"}
                        style={{
                          width: "100%",
                          height: 200,
                        }}
                    />
                </div>
            ),
            resizable: false,
            reorderable: false,
        }
    ];

    const getData = () => {
        fetch(`/api/administrator/home`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setTicketingWebsiteAlertIssues(result.ticketingWebsiteAlertIssues);
                setEventAlerts(result.recentVenueEventAlerts);
                setEventStatusChanges(result.recentVenueEventStatusChanges);

                setIsLoading(false);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    }

    useEffect(() => {
        document.title = "Administrator Dashboard";

        getData();
    }, []);

    return (
        <>
            {isLoading ? 
                <TileLayout
                    autoFlow="row dense"
                    columns={1}
                    items={skeletonTiles}
                />
            : 
                <div className="m-3">
                    <Card className="mb-3">
                        <CardHeader>
                            <CardTitle>
                                Event alerts in last 24 hours
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <Grid
                                data={eventAlerts.slice(eventAlertsSkipCount, itemsPerPage + eventAlertsSkipCount)}
                                resizable={false}
                                skip={eventAlertsSkipCount}
                                take={itemsPerPage}
                                total={eventAlerts.length}
                                pageable={true}
                                onPageChange={eventAlertsPageChange}
                                scrollable="none"
                            >
                                <Column field="dateCreated" title={"Created"} />
                                <Column field="eventName" title={"Event"} />
                                <Column field="venueName" title={"Venue"} />
                                <Column field="eventDate" title={"Event Date"} />
                                <Column field="venueEventStatusTypeName" title={"Status"} />
                                <Column field="sectionName" title={"Section"} />
                                <Column field="venueEventAlertTypeName" title={"Type"} />    
                                <Column field="ticketCount" title={"Count"} />
                                <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />
                            </Grid>
                        </CardBody>
                    </Card>
                    <Card className="mb-3">
                        <CardHeader>
                            <CardTitle>
                                Event status changes in last 24 hours
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <Grid
                                data={eventStatusChanges.slice(eventStatusChangesSkipCount, itemsPerPage + eventStatusChangesSkipCount)}
                                resizable={false}
                                skip={eventStatusChangesSkipCount}
                                take={itemsPerPage}
                                total={eventStatusChanges.length}
                                pageable={true}
                                onPageChange={eventStatusChangesPageChange}
                                scrollable="none"
                            >
                                <Column field="dateCreated" title={"Created"} />
                                <Column field="eventName" title={"Event"} />
                                <Column field="venueName" title={"Venue"} />
                                <Column field="eventDate" title={"Event Date"} />
                                <Column field="currentVenueEventStatusTypeName" title={"Current Status"} />
                                <Column field="oldVenueEventStatusTypeName" title={"Old Status"} />
                                <Column field="newVenueEventStatusTypeName" title={"New Status"} /> 
                                <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />
                            </Grid>
                        </CardBody>
                    </Card>
                    <Card className="mb-3">
                        <CardHeader>
                            <CardTitle>
                                Unfixed TWA Issues
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <Grid
                                data={ticketingWebsiteAlertIssues.slice(ticketingWebsiteAlertIssuesSkipCount, itemsPerPage + ticketingWebsiteAlertIssuesSkipCount)}
                                resizable={false}
                                skip={ticketingWebsiteAlertIssuesSkipCount}
                                take={itemsPerPage}
                                total={ticketingWebsiteAlertIssues.length}
                                pageable={true}
                                onPageChange={ticketingWebsiteAlertIssuesPageChange}
                                scrollable="none"
                            >
                                <Column field="ticketingWebsiteName" title={"Website"} />
                                <Column field="ticketingWebsiteAlertLabel" title={"Alert Label"} />
                                <Column field="dateCreated" title={"Date"} />
                                <Column field="isAlertSuccessful" title={"Alert Successful"} />
                                <Column field="isEmailSentSuccessfully" title={"Email Successful"} />
                                <Column field="notes" title={"Notes"} cell={ViewDialogCell} />
                            </Grid>
                        </CardBody>
                    </Card>
                </div>
            }
            {showDialog === false ? <></> :
                <Dialog
                    title={dialogTitle}
                    onClose={() => setShowDialog(false)}
                    width={"90vw"}
                >
                    {dialogContent}
                </Dialog>
            }
        </>
    );
}